import { useCallback, useEffect, useRef, useState } from 'react';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { GalleryAsset } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { thumbnailUrl } from '../gallery/api';
import { ZoomableImage } from '../viewer/ZoomableImage';
import { originalUrl } from '../viewer/viewer-api';
import type { ViewerPosition } from '../viewer/Viewer';
import { TagPanel, type TagPanelHandle } from './TagPanel';
import { clearPrefetchedTagging, prefetchAssetTagging } from './tagging-prefetch';

/**
 * TAGUEUR par photo (mode « tag » de la visionneuse). Image GRANDE et ZOOMABLE à gauche,
 * panneau de tags à droite (en dessous en mobile). On coche/décoche, « Valider et suivant »
 * commit le set validé puis AVANCE ; les flèches ‹ › SAUTENT sans valider.
 *
 * NAVIGATION-AGNOSTIQUE (comme `Viewer`) : le média courant + la navigation préc./suiv. sont
 * FOURNIS par `ViewerRoute` (mode série). Modale accessible (`role="dialog"`, focus initial +
 * restauré, défilement du corps verrouillé, Échap ferme).
 *
 * ⚠ CLAVIER : contrairement au `Viewer`, ce composant N'INSTALLE PAS de handler global ←/→
 * (ni de diaporama) — les flèches sont des boutons explicites. Ainsi, taper/espace dans une
 * case du panneau NE navigue PAS (le focus dans le panneau garde ses propres événements) ;
 * seul Échap ferme.
 */

interface TagWalkProps {
  /** Média courant à taguer (`undefined` = en cours de chargement). */
  asset: GalleryAsset | undefined;
  /** Position dans la série (compteur), ou `null` en accès dégradé. */
  position: ViewerPosition | null;
  /** Va au média précédent SANS valider (no-op si indisponible). */
  onPrev: () => void;
  /** Va au média suivant SANS valider (no-op si indisponible). */
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
  /**
   * Id de la photo SUIVANTE de la série, ou `null` en fin de série. Sert au
   * PRÉCHARGEMENT de son tagging (audit A5) : la marche « Valider et suivant » était
   * entièrement séquentielle (POST → navigation → GET bloquant), d'où un
   * « Chargement… » entre chaque photo sur une révision de plusieurs centaines.
   */
  nextId?: string | null;
  /** Ferme le tagueur (revient à la galerie / au chemin de retour). */
  onClose: () => void;
}

export function TagWalk({
  asset,
  position,
  onPrev,
  onNext,
  canPrev,
  canNext,
  nextId = null,
  onClose,
}: TagWalkProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const panelRef = useRef<TagPanelHandle>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, initialFocusRef: dialogRef, onEscape: onClose });
  const [busy, setBusy] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  // Réinitialise l'échec d'image à chaque changement de média.
  useEffect(() => {
    setImgFailed(false);
  }, [asset?.id]);

  // Précharge le tagging de la photo SUIVANTE dès que la COURANTE est prête (audit A5).
  // On attend « prête » plutôt que de tirer les deux de front : la photo affichée doit
  // garder la priorité réseau (même discipline que le préchargement directionnel des
  // originaux dans `use-viewer-navigation`).
  const prefetchNext = useCallback(() => {
    if (nextId !== null) prefetchAssetTagging(nextId);
  }, [nextId]);

  // Sortie du Tagueur : la réserve de préchargement n'a plus d'objet (et une révision
  // ultérieure doit repartir de l'état SERVEUR, pas d'une réponse d'il y a dix minutes).
  useEffect(() => clearPrefetchedTagging, []);

  // Verrou de défilement + focus initial + restauration à la fermeture.

  // Échap ferme (aucun autre raccourci global : les flèches sont des boutons).

  // « Valider et suivant » : commit le set coché (via le handle du panneau) puis avance
  // (ou ferme sur la dernière photo). Un 403 lecture seule affiche son message dans le
  // panneau et NE fait pas avancer.
  const validateAndAdvance = useCallback(async (): Promise<void> => {
    if (busy || !asset) return;
    setBusy(true);
    const outcome = await panelRef.current?.commit();
    setBusy(false);
    if (outcome === 'ok') {
      if (canNext) onNext();
      else onClose();
    }
  }, [busy, asset, canNext, onNext, onClose]);

  const counter = position
    ? interpolate(t('viewer.counter'), { index: position.index + 1, total: position.total }, locale)
    : null;
  // Dernière photo de la série → le bouton commit puis FERME (libellé « … et terminer »).
  const isLast = position ? position.index >= position.total - 1 : !canNext;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('tagging.title')}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-overlay/95 text-white outline-none"
    >
      {/* Titre de niveau 1 de l'écran : le Tagueur n'avait qu'un `<h2>` (le panneau de
          taxonomie), sans `<h1>` au-dessus. Visuel déjà porté par le compteur → `sr-only`. */}
      <h1 className="sr-only">{t('tagging.title')}</h1>

      {/* Barre supérieure : compteur + préc./suiv. (saut) + fermer. */}
      <div className="flex items-center justify-between gap-2 p-2">
        <span className="px-2 text-sm tabular-nums" aria-live="polite">
          {counter}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            title={t('viewer.previous')}
            aria-label={t('viewer.previous')}
            className="rounded-md p-2 hover:bg-white/10 disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            title={t('viewer.next')}
            aria-label={t('viewer.next')}
            className="rounded-md p-2 hover:bg-white/10 disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onClose}
            title={t('viewer.close')}
            aria-label={t('viewer.close')}
            className="rounded-md p-2 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Corps : média (gauche, zoomable) + panneau de tags (droite ; EN DESSOUS en mobile). */}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <div className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center p-2">
          {asset === undefined ? (
            <p className="text-sm text-white/70" role="status">
              {t('viewer.loading')}
            </p>
          ) : imgFailed ? (
            <div className="flex flex-col items-center gap-2 text-white/70">
              <ExclamationTriangleIcon className="h-10 w-10" aria-hidden="true" />
              <p>{t('viewer.error')}</p>
            </div>
          ) : asset.kind === 'image' ? (
            <ZoomableImage
              key={asset.id}
              src={originalUrl(asset.id)}
              alt={asset.filename}
              naturalWidth={asset.width}
              naturalHeight={asset.height}
              onError={() => setImgFailed(true)}
            />
          ) : (
            // Vidéo : lue sur l'original en muet/autoplay/boucle, contrôles natifs.
            <video
              key={asset.id}
              src={originalUrl(asset.id)}
              poster={thumbnailUrl(asset.id, 1024)}
              aria-label={asset.filename}
              muted
              autoPlay
              loop
              controls
              playsInline
              onError={() => setImgFailed(true)}
              className="max-h-full max-w-full object-contain"
            >
              {t('viewer.videoUnsupported')}
            </video>
          )}
        </div>

        {/* Panneau de tags : colonne de droite en desktop, bandeau en bas (borné) en mobile. */}
        <aside className="flex max-h-[45vh] w-full shrink-0 flex-col overflow-hidden border-t border-white/10 bg-surface text-fg md:max-h-none md:w-80 md:border-s md:border-t-0">
          <div className="border-b border-border p-3">
            <h2 className="text-base font-semibold text-fg">{t('tagging.panelTitle')}</h2>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {asset && (
              <TagPanel key={asset.id} ref={panelRef} assetId={asset.id} onReady={prefetchNext} />
            )}
          </div>
          <div className="border-t border-border p-3">
            <button
              type="button"
              onClick={() => void validateAndAdvance()}
              disabled={busy || !asset}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-brand px-4 py-2.5 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy && (
                <ArrowPathIcon
                  className="h-5 w-5 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              )}
              {busy
                ? t('tagging.validating')
                : isLast
                  ? t('tagging.validateAndFinish')
                  : t('tagging.validateAndNext')}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
